import { useState } from "react";
import { DEFAULT_GEN, entropyBits, generate, strength, type GenOptions } from "../lib/generator";
import { useVault } from "../state/store";
import { Badge, Button, Eyebrow, Switch, Tabs } from "../ui/ds";
import { usePerspective, useUI } from "./ui";

export function GeneratorView() {
  const v = useVault();
  const p = usePerspective();
  const { setModal } = useUI();
  const [g, setG] = useState<GenOptions>(DEFAULT_GEN);
  const [val, setVal] = useState(() => generate(DEFAULT_GEN));
  const set = (patch: Partial<GenOptions>) => {
    const next = { ...g, ...patch };
    setG(next);
    setVal(generate(next));
  };
  const bits = entropyBits(g);
  const s = strength(bits);
  const isPw = g.mode === "pw";
  const personal = p.vaults.find((x) => x.kind === "personal");

  return (
    <div className="scroll">
      <div className="page-head">
        <Eyebrow size="sm">Tools</Eyebrow>
        <h1 className="page-h1" style={{ marginBottom: 0 }}>
          Password generator
        </h1>
      </div>
      <div className="page-body">
        <div className="card" style={{ maxWidth: 640, padding: 24, display: "flex", flexDirection: "column", gap: 20 }}>
          <div className="accent-rule" />
          <Tabs variant="pill" items={["Password", "Passphrase"]} value={isPw ? "Password" : "Passphrase"} onChange={(m) => set({ mode: m === "Password" ? "pw" : "phrase" })} />
          <div className="gen-out" aria-live="polite">
            {val}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <Badge tone={s.tone} dot>
              {s.label}
            </Badge>
            <span className="mono" style={{ fontSize: 12, color: "var(--text-muted)" }}>
              {bits} bits of entropy
            </span>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <label htmlFor="gen-range" style={{ display: "flex", justifyContent: "space-between", fontSize: 13, fontWeight: 600, color: "var(--ink-navy)" }}>
              <span>{isPw ? "Length" : "Words"}</span>
              <span className="mono">{isPw ? g.len : g.words}</span>
            </label>
            <input
              id="gen-range"
              type="range"
              min={isPw ? 8 : 3}
              max={isPw ? 64 : 10}
              value={isPw ? g.len : g.words}
              onChange={(e) => set(isPw ? { len: +e.target.value } : { words: +e.target.value })}
            />
          </div>
          {isPw ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <Switch label="Uppercase A–Z" checked={g.upper} onChange={(x) => set({ upper: x })} />
              <Switch label="Numbers 2–9" checked={g.num} onChange={(x) => set({ num: x })} />
              <Switch label="Symbols !@#$%" checked={g.sym} onChange={(x) => set({ sym: x })} />
            </div>
          ) : null}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", borderTop: "1px solid var(--border-default)", paddingTop: 20 }}>
            <Button icon="copy" onClick={() => void v.copy(val, isPw ? "Password" : "Passphrase")}>
              Copy
            </Button>
            <Button variant="secondary" icon="refresh-cw" onClick={() => setVal(generate(g))}>
              Regenerate
            </Button>
            {personal ? (
              <Button variant="ghost" icon="key-round" onClick={() => setModal({ kind: "item", itemId: null, vaultId: personal.id, preset: { type: "login", password: val } })}>
                Save as login
              </Button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
